import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { filter, map, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';

import { DsButtonVariants } from '@levelaccess/design-system';
import { RoutingService } from '../../../../services/routing.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import {
  $digitalProperty,
  $digitalPropertyDefaultTool,
  $digitalPropertyType,
  CHAR_LIMITS,
  DigitalPropertyStatus,
  ISelectAuditTool,
  SWA_MAX_EXTRA_TIME_SECONDS,
} from '../../../../../../shared/constants/digital-properties';
import {
  IDigitalProperty,
  IDigitalPropertyDetails,
  IDigitalPropertyListItem,
  IDigitalPropertyType,
} from '../../../../../../shared/interfaces/digital-property.interface';
import { TranslateService } from '../../../../translate/translate.service';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { $tenant, $privacySettings } from '../../../../../../shared/constants/tenant';
import { IWorkspace } from '../../../../../../shared/interfaces/workspace.interface';
import { DigitalPropertyService } from '../../../../services/digital-property.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { NotificationPosition } from '../../../../models/notification.model';
import { NotificationService } from '../../../../services/notification.service';
import { A11yService } from '../../../../services/a11y.service';
import { CustomValidators } from '../../../../services/helpers/form-custom-validators';
import { alertType } from '../../../../constants/alert.constants';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { AuditToolCommonNames, mapAuditToolCommonNamesToDisplayName } from '../../../../../../shared/constants/audit-tool';
import { SharedTextUtility } from '../../../../../../shared/utils/text.utility';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { AngularUtility } from '../../../../utility/angular.utility';
import {
  MAX_DIGITAL_PROPERTY_TAG_CHAR_LENGTH,
  MAX_DIGITAL_PROPERTY_TAGS,
} from '../../../../constants/digital-properties-tags.constants';
import { DigitalPropertyTypeUtility } from '../../../../../../shared/utils/digital-property-type.utility';
import { AppConfigService } from '../../../../services/app-config.service';
import { UserAclService } from '../../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { CommonUtility } from '../../../../utility/common.utility';

const defaultTool: AuditToolCommonNames = $digitalPropertyDefaultTool;

@Component({
  selector: 'app-edit-tenant-workspace-digital-property-form',
  templateUrl: './edit-tenant-workspace-digital-property-form.component.html',
  styleUrls: ['./edit-tenant-workspace-digital-property-form.component.scss'],
  standalone: false,
})
export class EditTenantWorkspaceDigitalPropertyFormComponent implements OnInit, OnDestroy {
  @Input()
  public set digitalPropertyData(dp: IDigitalPropertyDetails) {
    this.digitalProperty = dp;
    this.digitalPropertyData$.next();
  }
  @Input() modal: boolean = false;
  @Input() isWebsite: boolean = false;
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  @Output() public digitalPropertyAction$: EventEmitter<IDigitalProperty>;

  private subscriptions: Subscription;
  private formValidationRequest: Subject<void>;
  private digitalPropertyData$: ReplaySubject<IDigitalProperty>;
  private digitalPropertyTypes: IDigitalPropertyType[];

  public showWebsiteFields: boolean;
  public digitalProperty: IDigitalPropertyDetails;
  public errorResponseMessages: Array<string>;
  public alertType: typeof alertType;
  public pageTitle: string;
  public form: UntypedFormGroup;
  public $digitalProperty: typeof $digitalProperty;
  public $digitalPropertyType: typeof $digitalPropertyType;
  public formValidationRequest$: Observable<void>;
  public userActiveDigitalProperty: IDigitalPropertyListItem;
  public digitalPropertyTypes$: BehaviorSubject<IDigitalPropertyType[]>;
  public onFindUserErrorMessage: string;
  public hasAccessibilityStatement: boolean;
  public auditToolSelectOptions: ISelectAuditTool[];

  public charLimits: typeof CHAR_LIMITS = CHAR_LIMITS;
  public tagsFeatureEnabled: boolean;
  public maxDigitalTag: number = MAX_DIGITAL_PROPERTY_TAGS;
  public maxDigitalTagCharLength: number = MAX_DIGITAL_PROPERTY_TAG_CHAR_LENGTH;
  public scanningAgentFeatureEnabled: boolean;
  public digitalAssetsPrivacySettingFeatureEnabled: boolean;
  public contentsquareIntegrationFeatureEnabled: boolean;
  public isMobile: boolean = false;
  public isAdminView: boolean;
  public confidentialScanningEnabledTenantLevel: boolean = false;
  public debouncedSubmitAction: () => void;

  constructor(
    private routingService: RoutingService,
    private errorMessageService: ErrorMessageService,
    private element: ElementRef<Element>,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private digitalPropertyService: DigitalPropertyService,
    private notificationService: NotificationService,
    private a11yService: A11yService,
    private changeDetectorRef: ChangeDetectorRef,
    private featureFlagService: FeatureFlagService,
    private workspaceService: WorkspaceService,
    private appConfigService: AppConfigService,
    private userAclService: UserAclService,
  ) {
    this.alertType = alertType;
    this.isAdminView = this.appConfigService.isTenantEnabled() === false;
    this.errorResponseMessages = [];
    this.subscriptions = new Subscription();
    this.pageTitle = '';
    this.formValidationRequest = new Subject<void>();
    this.formValidationRequest$ = this.formValidationRequest.asObservable();
    this.$digitalProperty = $digitalProperty;
    this.digitalPropertyAction$ = new EventEmitter();
    this.digitalPropertyData$ = new ReplaySubject<IDigitalProperty>();
    this.userActiveDigitalProperty = null;
    this.$digitalPropertyType = $digitalPropertyType;
    this.digitalPropertyTypes$ = new BehaviorSubject([]);
    this.onFindUserErrorMessage = '';
    this.digitalProperty = null;
    this.digitalPropertyTypes = null;
    this.hasAccessibilityStatement = false;
    this.auditToolSelectOptions = Object.entries(mapAuditToolCommonNamesToDisplayName)
      .filter(([toolName, _]: [AuditToolCommonNames, string]): boolean => toolName !== AuditToolCommonNames.aslint)
      .map(([toolName, toolDisplayName]: [AuditToolCommonNames, string]): ISelectAuditTool => {
        return {
          displayName: this.translateService.instant(toolDisplayName),
          value: toolName,
        };
      });

    this.tagsFeatureEnabled = this.featureFlagService.variation(FeatureFlagCollection.digitalPropertyTagging, false);
    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.dataEnrichmentMicroserviceIntegration, false)
        .pipe(map(Boolean), AngularUtility.shareRef())
        .subscribe((enabled: boolean) => {
          this.contentsquareIntegrationFeatureEnabled = enabled;
        }),
    );
    this.subscriptions.add(
      this.featureFlagService.variation$(FeatureFlagCollection.scanningAgent, false).subscribe((enabled: boolean) => {
        this.scanningAgentFeatureEnabled = enabled;
      }),
    );
    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.digitalAssetsPrivacySetting, false)
        .subscribe((enabled: boolean) => {
          this.digitalAssetsPrivacySettingFeatureEnabled = enabled;
        }),
    );
    this.subscribeToIncomingFormData();

    this.debouncedSubmitAction = CommonUtility.createDebouncedSubmit(() => this.handleSubmit());
  }

  private handleSubmit(): void {
    this.formValidationRequest.next();
    this.changeDetectorRef.detectChanges();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    this.digitalPropertyAction$.emit(this.getFormData());
  }

  private subscribeToIncomingFormData(): void {
    const onIncomingTestingData$: Subscription = this.digitalPropertyData$
      .pipe(filter(() => SharedCommonUtility.notNullish(this.digitalProperty)))
      .subscribe(() => {
        this.createForm();
        this.getDigitalPropertyTypes();
      });

    this.subscriptions.add(onIncomingTestingData$);
  }

  private getUrlValidators(): ValidatorFn[] {
    return [CustomValidators.required, CustomValidators.validateIsEmpty, CustomValidators.domainValidator];
  }

  private createForm(): void {
    if (this.digitalProperty === null) {
      return;
    }

    this.isMobile = DigitalPropertyTypeUtility.isMobile(this.digitalProperty[$digitalProperty.type]);

    const typeValue: string = this.digitalProperty[$digitalProperty.typeId];
    const workspaceValue: string | null =
      this.digitalProperty[$digitalProperty.workspace] === null
        ? null
        : this.digitalProperty[$digitalProperty.workspace][$workspace.name];

    const formConfig: Partial<Record<$digitalProperty, AbstractControl>> = {
      [$digitalProperty.name]: this.formBuilder.control(this.digitalProperty[$digitalProperty.name]),
      [$digitalProperty.url]: this.formBuilder.control(this.digitalProperty[$digitalProperty.url], this.getUrlValidators()),
      [$digitalProperty.propertyKey]: this.formBuilder.control({
        value: this.digitalProperty[$digitalProperty.propertyKey],
        disabled: true,
      }),
      [$digitalProperty.pagesLimit]: this.formBuilder.control({
        value: this.digitalProperty[$digitalProperty.pagesLimit],
        disabled: true,
      }),
      [$digitalProperty.typeId]: this.formBuilder.control(typeValue, CustomValidators.validateIsEmpty),
      [$digitalProperty.workspace]: new UntypedFormControl({ value: workspaceValue, disabled: true }),
      [$digitalProperty.authorizedDomains]: new UntypedFormControl(
        {
          value: this.digitalProperty[$digitalProperty.authorizedDomains].join('\n'),
          disabled: this.digitalProperty[$digitalProperty.noDomainRestrictions],
        },
        [CustomValidators.validateIsEmpty, CustomValidators.domainValidator],
      ),
      [$digitalProperty.noDomainRestrictions]: this.formBuilder.control(
        this.digitalProperty[$digitalProperty.noDomainRestrictions],
      ),
      [$digitalProperty.defaultTool]: this.formBuilder.control(this.digitalProperty[$digitalProperty.defaultTool]),
      [$digitalProperty.extraSwaWait]: this.formBuilder.control(
        this.digitalProperty[$digitalProperty.timeoutSettings]?.[$digitalProperty.extraSwaWait] ?? 0,
        [CustomValidators.min(0), CustomValidators.max(SWA_MAX_EXTRA_TIME_SECONDS)],
      ),
      [$digitalProperty.useScanningAgent]: this.formBuilder.control(
        this.digitalProperty[$digitalProperty.useScanningAgent] ?? false,
      ),
      [$digitalProperty.privacyObfuscationEnabled]: this.formBuilder.control(
        this.digitalProperty[$digitalProperty.privacyObfuscationEnabled] ?? false,
      ),
    };

    if (this.contentsquareIntegrationFeatureEnabled) {
      formConfig[$digitalProperty.contentsquareIntegration] = this.formBuilder.control(
        this.digitalProperty[$digitalProperty.contentsquareIntegration] ?? false,
      );
    }

    this.form = this.formBuilder.group(formConfig);

    if (this.tagsFeatureEnabled) {
      this.form.addControl($digitalProperty.tags, this.formBuilder.control(this.digitalProperty?.[$digitalProperty.tags] || []));
    }

    if (this.isMobile) {
      const existingBundleId: string | undefined = this.digitalProperty[$digitalProperty.mobileBundleId];

      this.form.addControl(
        $digitalProperty.mobileBundleId,
        this.formBuilder.control(existingBundleId ?? '', {
          validators: [CustomValidators.validateIsEmpty, CustomValidators.mobileBundleIdValidator],
        }),
      );

      this.form.addControl(
        $digitalProperty.noBundleIdRestriction,
        this.formBuilder.control(this.digitalProperty[$digitalProperty.noBundleIdRestriction] ?? false),
      );
    }

    this.hasAccessibilityStatement = Boolean(this.digitalProperty[$digitalProperty.accessibilityStatement]);
    this.onAccessibilityStatementSelectorChange(this.hasAccessibilityStatement);

    this.subscriptions.add(
      this.form.get($digitalProperty.typeId).valueChanges.subscribe(this.onDigitalPropertyTypeChange.bind(this)),
    );

    this.subscriptions.add(
      this.form.get($digitalProperty.noDomainRestrictions).valueChanges.subscribe(this.onNoDomainRestrictionsChange.bind(this)),
    );

    this.subscriptions.add(
      this.digitalPropertyService
        .existingKeyValues(
          this.digitalProperty[$digitalProperty.workspace][$workspace._id],
          $digitalProperty.accessibilityStatement,
          this.digitalProperty[$digitalProperty._id],
        )
        .subscribe(() => {
          if (this.digitalProperty && this.digitalProperty[$digitalProperty.accessibilityStatement]) {
            this.hasAccessibilityStatement = true;
            this.onAccessibilityStatementSelectorChange(true);
          }
        }),
    );

    this.loadExistingNamesAndUpdateValidators();
    this.checkTenantConfidentialScanning();
  }

  private checkTenantConfidentialScanning(): void {
    this.subscriptions.add(
      this.workspaceService
        .getWorkspace(this.digitalProperty[$digitalProperty.workspaceId])
        .pipe(take(1))
        .subscribe((workspace: IWorkspace): void => {
          const enabled: boolean =
            workspace?.[$workspace.tenant]?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] === true;

          this.confidentialScanningEnabledTenantLevel = enabled;

          if (enabled) {
            this.form.get($digitalProperty.privacyObfuscationEnabled)?.setValue(true);
          }

          this.changeDetectorRef.detectChanges();
        }),
    );
  }

  private loadExistingNamesAndUpdateValidators(): void {
    const subscription: Subscription = this.digitalPropertyService
      .existingKeyValues(
        this.digitalProperty[$digitalProperty.workspaceId],
        $digitalProperty.name,
        this.digitalProperty[$digitalProperty._id],
      )
      .subscribe(this.updatePropertyNameValidators.bind(this));

    this.subscriptions.add(subscription);
  }

  private updatePropertyNameValidators(existingNames: string[]): void {
    const nameControl: AbstractControl = this.form.get($digitalProperty.name);

    nameControl.setValidators([
      CustomValidators.required,
      CustomValidators.validateIsEmpty,
      CustomValidators.maxLength(CHAR_LIMITS[$digitalProperty.name]),
      CustomValidators.digitalPropertyFieldWorkspaceDuplicateValidator(existingNames, $digitalProperty.name),
    ]);
    nameControl.updateValueAndValidity();
    this.formValidationRequest.next();
  }

  private getAuthorizedDomains(domainsText: string): string[] {
    if (this.hasNoDomainRestrictions()) {
      return [];
    }

    return SharedTextUtility.getLinesFromText(domainsText);
  }

  private getFormData(): IDigitalProperty {
    const propWorkspace: IWorkspace = this.digitalProperty[$digitalProperty.workspace] as unknown as IWorkspace;

    const formData: IDigitalProperty = {
      [$digitalProperty.name]: this.form.get($digitalProperty.name).value,
      [$digitalProperty.url]: SharedCommonUtility.notNullish(this.form.get($digitalProperty.url).value)
        ? this.form.get($digitalProperty.url).value
        : undefined,
      [$digitalProperty.typeId]: this.form.get($digitalProperty.typeId).value,
      [$digitalProperty.propertyKey]: this.form.get($digitalProperty.propertyKey).value,
      [$digitalProperty.pagesLimit]: Number(this.form.get($digitalProperty.pagesLimit).value),
      [$digitalProperty.workspaceId]: propWorkspace[$workspace._id],
      [$digitalProperty.authorizedDomains]: this.getAuthorizedDomains(this.form.get($digitalProperty.authorizedDomains).value),
      [$digitalProperty.noDomainRestrictions]: this.hasNoDomainRestrictions(),
      [$digitalProperty.status]: DigitalPropertyStatus.active,
      [$digitalProperty.defaultTool]: this.form.get($digitalProperty.defaultTool).value ?? defaultTool,
      [$digitalProperty.timeoutSettings]: {
        [$digitalProperty.extraSwaWait]: Number(this.form.get($digitalProperty.extraSwaWait).value) || 0,
      },
      [$digitalProperty.accessibilityStatement]: this.form.get($digitalProperty.accessibilityStatement)
        ? this.form.get($digitalProperty.accessibilityStatement).value
        : undefined,
      [$digitalProperty.useScanningAgent]: this.form.get($digitalProperty.useScanningAgent)
        ? this.form.get($digitalProperty.useScanningAgent).value
        : undefined,
      [$digitalProperty.privacyObfuscationEnabled]: this.form.get($digitalProperty.privacyObfuscationEnabled)
        ? this.form.get($digitalProperty.privacyObfuscationEnabled).value
        : undefined,
      [$digitalProperty.contentsquareIntegration]: this.form.get($digitalProperty.contentsquareIntegration)
        ? this.form.get($digitalProperty.contentsquareIntegration).value
        : undefined,
      [$digitalProperty.mobileBundleId]: this.form.get($digitalProperty.mobileBundleId)
        ? this.form.get($digitalProperty.mobileBundleId).value
        : undefined,
      [$digitalProperty.noBundleIdRestriction]: this.form.get($digitalProperty.noBundleIdRestriction)
        ? this.form.get($digitalProperty.noBundleIdRestriction).value
        : undefined,
    };

    if (this.tagsFeatureEnabled && this.form?.contains($digitalProperty.tags)) {
      formData[$digitalProperty.tags] = this.form?.get($digitalProperty.tags).value || [];
    }

    return formData;
  }

  public onAccessibilityStatementSelectorChange(enabled: boolean): void {
    if (enabled) {
      this.form.registerControl(
        $digitalProperty.accessibilityStatement,
        this.formBuilder.control((this.digitalProperty && this.digitalProperty[$digitalProperty.accessibilityStatement]) || '', {
          validators: this.getUrlValidators(),
        }),
      );
    } else {
      this.form.removeControl($digitalProperty.accessibilityStatement);
    }
  }

  private setDigitalPropertyTypes(digitalPropertyTypes: IDigitalPropertyType[]): void {
    this.digitalPropertyTypes = digitalPropertyTypes;
    this.digitalPropertyTypes$.next(digitalPropertyTypes);
    if (this.form && this.form.get($digitalProperty.typeId).value === null && digitalPropertyTypes.length > 0) {
      this.form.get($digitalProperty.typeId).setValue(digitalPropertyTypes[0]._id);
    }
    this.onDigitalPropertyTypeChange();
  }

  private getDigitalPropertyTypesSuccess(digitalPropertyTypes: IDigitalPropertyType[]): void {
    this.setDigitalPropertyTypes(digitalPropertyTypes);
  }

  private getDigitalPropertyTypesError(response: HttpErrorResponse): void {
    this.handleError('get_digital_property_types_error', response);
  }

  private getDigitalPropertyTypes(): void {
    this.subscriptions.add(
      this.digitalPropertyService.getDigitalPropertyTypes().subscribe({
        next: this.getDigitalPropertyTypesSuccess.bind(this),
        error: this.getDigitalPropertyTypesError.bind(this),
      }),
    );
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);
    if (typeof errorMessage === 'string') {
      this.errorResponseMessages.push(this.translateService.instant(errorMessage));
    }

    this.notificationService.error(appMessage + ': ' + errorMessage, NotificationPosition.Toast, true);

    console.error('[DigitalPropertyFormComponent.handleError: ' + appMessage + ']', response, errorMessage);
  }

  public get confidentialScanningTooltip(): string | undefined {
    return this.confidentialScanningEnabledTenantLevel ? 'confidential_scanning_tenant_level_tooltip' : undefined;
  }

  public get canUpdateDigitalProperties$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useAdmin()
            .useFunctionalActions(RequiredSecurities.Digital_Properties_Update.functionalActions)
            .useForNewSecurityOnly()
            .check(),
        ),
      );
  }

  public onDigitalPropertyTypeChange(): void {
    if (this.isDomainRestrictionSupported()) {
      this.form
        .get($digitalProperty.url)
        .setValidators([CustomValidators.required, CustomValidators.validateIsEmpty, CustomValidators.domainValidator]);
    } else {
      this.form.get($digitalProperty.url).clearValidators();
    }
    this.form.get($digitalProperty.url).updateValueAndValidity();
  }

  public onNoDomainRestrictionsChange(): void {
    const domainsControl: UntypedFormControl = this.form.get($digitalProperty.authorizedDomains) as UntypedFormControl;
    if (this.hasNoDomainRestrictions()) {
      domainsControl.disable();
    } else {
      domainsControl.enable();

      const urlControl: AbstractControl = this.form.get($digitalProperty.url);

      if (typeof domainsControl.value !== 'string' || domainsControl.value.trim().length !== 0) {
        return;
      }

      if (typeof urlControl.value !== 'string' || urlControl.value.trim().length === 0) {
        return;
      }

      domainsControl.setValue(SharedCommonUtility.getDomainPattern(urlControl.value).join('\n'));
    }
  }

  public hasNoDomainRestrictions(): boolean {
    const isDomainRestrictionSupported = this.isDomainRestrictionSupported();
    if (isDomainRestrictionSupported === false) {
      return true;
    }

    return this.form.get($digitalProperty.noDomainRestrictions).value;
  }

  public isDomainRestrictionSupported(): boolean {
    const digitalPropertyType: string = this.form.get($digitalProperty.typeId).value;

    if (digitalPropertyType === null || this.digitalPropertyTypes === null) {
      return false;
    }

    const selectedDigitalPropertyType: IDigitalPropertyType = this.digitalPropertyTypes.find(
      (_dpt: IDigitalPropertyType) => _dpt[$digitalPropertyType._id] === digitalPropertyType,
    );
    return selectedDigitalPropertyType !== undefined && selectedDigitalPropertyType[$digitalPropertyType.canRestrictDomain];
  }

  public onCancel(): void {
    if (this.modal) {
      this.cancelled.emit();
    }
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.digitalPropertyTagging, false)
        .pipe(map(Boolean), AngularUtility.shareRef())
        .subscribe((enabled: boolean) => {
          this.tagsFeatureEnabled = enabled;

          if (this.form) {
            if (enabled && !this.form.contains($digitalProperty.tags)) {
              this.form.addControl(
                $digitalProperty.tags,
                this.formBuilder.control(this.digitalProperty?.[$digitalProperty.tags] || []),
              );
            } else if (!enabled && this.form.contains($digitalProperty.tags)) {
              this.form.removeControl($digitalProperty.tags);
            }
            this.changeDetectorRef.detectChanges();
          }
        }),
    );

    this.pageTitle = this.routingService.getPageTitle();

    this.showWebsiteFields = !this.modal || this.isWebsite;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
}
